/**
 * SM Beauty mobile/tablet header.
 *
 * The custom header is visual only. It reuses Warehouse's native predictive
 * search, menu drawer, cart count, and mini-cart so those behaviors remain
 * compatible with the theme and Shopify apps.
 */
(function () {
  'use strict';

  var BREAKPOINT = 999;
  var mobileHeader = null;
  var searchSlot = null;
  var searchWrapper = null;
  var searchOrigin = null;
  var nativeCartToggle = null;
  var nativeCartCount = null;
  var mobileCartButton = null;
  var mobileCartCount = null;
  var cartObserver = null;
  var rafId = null;
  var headerSection = null;
  var homeStickyStart = null;

  function isMobileHeaderViewport() {
    return window.innerWidth <= BREAKPOINT;
  }

  function findElements() {
    mobileHeader = document.querySelector('[data-sm-mobile-header]');

    if (!mobileHeader) {
      return false;
    }

    headerSection = mobileHeader.closest('.shopify-section') || mobileHeader.parentElement;
    searchSlot = mobileHeader.querySelector('[data-sm-mobile-search-slot]');
    searchWrapper = document.querySelector(
      '.sm-header-root > .header .header__search-bar-wrapper'
    ) || document.querySelector('.header__search-bar-wrapper');

    nativeCartToggle = document.querySelector(
      '.sm-header-root > .header .header__action-item--cart .header__cart-toggle'
    ) || document.querySelector('.header__action-item--cart .header__cart-toggle');

    nativeCartCount = document.querySelector(
      '.sm-header-root > .header .header__cart-count'
    ) || document.querySelector('.header__cart-count');

    mobileCartButton = mobileHeader.querySelector('[data-sm-mobile-cart]');
    mobileCartCount = mobileHeader.querySelector('[data-sm-mobile-cart-count]');

    return true;
  }

  function createSearchOrigin() {
    if (!searchWrapper) {
      return;
    }

    if (searchOrigin && searchOrigin.parentNode) {
      return;
    }

    searchOrigin = document.createComment('sm-mobile-search-origin');
    searchWrapper.parentNode.insertBefore(searchOrigin, searchWrapper);
  }

  function moveNativeSearch() {
    if (!searchWrapper || !searchSlot) {
      return;
    }

    createSearchOrigin();

    var fallback = searchSlot.querySelector('[data-sm-mobile-search-fallback]');

    if (isMobileHeaderViewport()) {
      if (searchWrapper.parentNode !== searchSlot) {
        searchSlot.appendChild(searchWrapper);
      }

      searchWrapper.classList.add('sm-mobile-search-mounted');

      if (fallback) {
        fallback.hidden = true;
      }
    } else {
      if (searchOrigin && searchOrigin.parentNode) {
        searchOrigin.parentNode.insertBefore(searchWrapper, searchOrigin.nextSibling);
      }

      searchWrapper.classList.remove('sm-mobile-search-mounted');

      if (fallback) {
        fallback.hidden = false;
      }
    }
  }

  function openNativeSearch() {
    if (!searchWrapper) {
      window.location.href = (window.routes && window.routes.searchUrl) || '/search';
      return;
    }

    if (!isMobileHeaderViewport()) {
      return;
    }

    var input = searchWrapper.querySelector('input[name="q"]');

    if (input) {
      input.focus();
    }
  }

  function normalizeCount(value) {
    var parsed = parseInt(String(value || '').replace(/[^0-9]/g, ''), 10);
    return Number.isFinite(parsed) ? parsed : 0;
  }

  function syncCart() {
    if (!mobileCartButton || !mobileCartCount) {
      return;
    }

    nativeCartToggle = document.querySelector(
      '.sm-header-root > .header .header__action-item--cart .header__cart-toggle'
    ) || nativeCartToggle;

    nativeCartCount = document.querySelector(
      '.sm-header-root > .header .header__cart-count'
    ) || nativeCartCount;

    var count = nativeCartCount
      ? normalizeCount(nativeCartCount.textContent)
      : normalizeCount(window.theme && window.theme.cartCount);

    mobileCartCount.textContent = String(count);
    mobileCartCount.classList.toggle('is-empty', count === 0);

    if (nativeCartToggle) {
      var expanded = nativeCartToggle.getAttribute('aria-expanded') === 'true';
      mobileCartButton.setAttribute('aria-expanded', expanded ? 'true' : 'false');
    }
  }

  function observeCart() {
    if (cartObserver) {
      cartObserver.disconnect();
    }

    cartObserver = new MutationObserver(syncCart);

    var cartItem = document.querySelector(
      '.sm-header-root > .header .header__action-item--cart'
    );

    if (cartItem) {
      cartObserver.observe(cartItem, {
        subtree: true,
        childList: true,
        characterData: true,
        attributes: true,
        attributeFilter: ['aria-expanded']
      });
    }
  }

  function openCart(event) {
    event.preventDefault();

    nativeCartToggle = document.querySelector(
      '.sm-header-root > .header .header__action-item--cart .header__cart-toggle'
    ) || nativeCartToggle;

    if (nativeCartToggle) {
      nativeCartToggle.click();
      window.requestAnimationFrame(syncCart);
      return;
    }

    window.location.href =
      mobileHeader.getAttribute('data-cart-url') ||
      (window.routes && window.routes.cartUrl) ||
      '/cart';
  }

  function isHomePage() {
    return document.body && document.body.classList.contains('template-index');
  }

  function clearHomeSticky() {
    if (mobileHeader) {
      mobileHeader.classList.remove('is-home-fixed');
    }

    if (headerSection) {
      headerSection.classList.remove('sm-mobile-header-home-placeholder');
    }
  }

  function updateHomeSticky() {
    if (!mobileHeader) {
      return;
    }

    if (!isHomePage() || !isMobileHeaderViewport()) {
      clearHomeSticky();
      homeStickyStart = null;
      return;
    }

    if (!headerSection || !document.documentElement.contains(headerSection)) {
      headerSection = mobileHeader.closest('.shopify-section') || mobileHeader.parentElement;
    }

    if (homeStickyStart === null) {
      var anchor = headerSection || mobileHeader;
      homeStickyStart = Math.max(
        0,
        Math.round(anchor.getBoundingClientRect().top + window.scrollY)
      );
    }

    var shouldFix = window.scrollY >= homeStickyStart;

    mobileHeader.classList.toggle('is-home-fixed', shouldFix);

    if (headerSection) {
      headerSection.classList.toggle(
        'sm-mobile-header-home-placeholder',
        shouldFix
      );
    }
  }

  function updateGeometry() {
    rafId = null;

    updateHomeSticky();

    if (!mobileHeader || !isMobileHeaderViewport()) {
      document.documentElement.style.removeProperty('--sm-mobile-menu-top');
      return;
    }

    var inner = mobileHeader.querySelector('.sm-mobile-header__inner');
    var rect = mobileHeader.getBoundingClientRect();
    var measuredHeight = inner ? inner.getBoundingClientRect().height : 60;

    /* Never feed the outer header's height back into its own CSS variable.
       The predictive-search markup can briefly report a viewport-sized height
       before its stylesheet finishes loading, which caused the header to grow
       to hundreds of pixels. */
    var height = Math.min(88, Math.max(48, Math.round(measuredHeight || 60)));
    var menuTop = Math.max(0, Math.round(rect.top + height));

    document.documentElement.style.setProperty(
      '--sm-mobile-menu-top',
      menuTop + 'px'
    );
    document.documentElement.style.setProperty('--header-height', height + 'px');

    mobileHeader.classList.toggle('is-scrolled', window.scrollY > 4);
  }

  function requestGeometryUpdate() {
    if (rafId !== null) {
      return;
    }

    rafId = window.requestAnimationFrame(updateGeometry);
  }

  function refresh() {
    if (!findElements()) {
      return;
    }

    homeStickyStart = null;
    moveNativeSearch();
    syncCart();
    observeCart();
    updateHomeSticky();
    requestGeometryUpdate();
  }

  function attachEvents() {
    document.addEventListener('click', function (event) {
      var fallback = event.target.closest('[data-sm-mobile-search-fallback]');
      var cartButton = event.target.closest('[data-sm-mobile-cart]');

      if (fallback) {
        event.preventDefault();
        openNativeSearch();
      }

      if (cartButton) {
        openCart(event);
      }
    });

    window.addEventListener('resize', function () {
      homeStickyStart = null;
      moveNativeSearch();
      requestGeometryUpdate();
    });

    window.addEventListener('orientationchange', function () {
      window.setTimeout(function () {
        homeStickyStart = null;
        moveNativeSearch();
        requestGeometryUpdate();
      }, 120);
    });

    window.addEventListener('scroll', requestGeometryUpdate, { passive: true });
    window.addEventListener('pageshow', refresh);

    document.addEventListener('cart:refresh', function () {
      window.setTimeout(syncCart, 250);
    });

    document.addEventListener('cart:rerendered', function () {
      syncCart();
      observeCart();
    });
    document.addEventListener('product:added', function () {
      window.setTimeout(syncCart, 350);
    });

    document.addEventListener('shopify:section:load', function (event) {
      if (event.target && event.target.querySelector('[data-sm-mobile-header]')) {
        window.setTimeout(refresh, 0);
      }
    });
  }

  function init() {
    refresh();
    attachEvents();

    window.setTimeout(requestGeometryUpdate, 0);
    window.setTimeout(requestGeometryUpdate, 150);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
